import {useState, useSyncExternalStore} from 'react';
import Button from '../components/Button';
import Sheet from '../components/feedback/Sheet';

// Promise-based confirmation on a bottom sheet, replacing window.confirm. Every action that
// cannot be taken back (or takes a moment to undo) goes through here, with words that say what
// will happen and, where it helps, what the way back is:
//
//   if (await confirm({title: 'Delete this tell?', body: 'It goes to Recently deleted…', confirmLabel: 'Delete', destructive: true})) …
//
// Hand it `action` and the sheet does the work too: the confirm button shows its ring until the
// promise settles, so a slow network is visible rather than a dead tap, and the sheet closes
// after. The promise resolves true when the action ran (and did not throw), false on cancel.
//   await confirm({title: 'Delete this tell?', …, action: () => softDelete.mutateAsync(id)});
// <ConfirmHost /> is mounted once in App.

export interface ConfirmOptions {
    title: string;
    /** What will happen, in a sentence. */
    body?: string;
    /** Smaller print under the body: the way back, a limit, a reassurance. */
    help?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    /** Runs when confirmed, with the button busy until it settles. */
    action?: () => Promise<unknown> | unknown;
    /** Shown on the confirm button while `action` runs ("Deleting…"). */
    busyLabel?: string;
}

interface Pending extends ConfirmOptions {
    resolve: (ok: boolean) => void;
}

let pending: Pending | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

export const confirm = (options: ConfirmOptions): Promise<boolean> =>
    new Promise(resolve => {
        pending?.resolve(false);
        pending = {...options, resolve};
        emit();
    });

const settle = (ok: boolean) => {
    const current = pending;
    pending = null;
    emit();
    current?.resolve(ok);
};

const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

export function ConfirmHost() {
    const current = useSyncExternalStore(subscribe, () => pending, () => pending);
    const [running, setRunning] = useState(false);

    const go = async () => {
        if (!current) return;
        if (!current.action) {
            settle(true);
            return;
        }
        setRunning(true);
        try {
            await current.action();
            settle(true);
        } catch {
            // The mutation's own error handling (a toast, a field error) has already spoken.
            settle(false);
        } finally {
            setRunning(false);
        }
    };

    return (
        <Sheet open={current !== null} onClose={() => !running && settle(false)} title={current?.title} busy={running}>
            {current && (
                <div className="flex flex-col gap-4">
                    {(current.body || current.help) && (
                        <div className="flex flex-col gap-1">
                            {current.body && <p className="m-0 text-ink-2">{current.body}</p>}
                            {current.help && <p className="meta m-0">{current.help}</p>}
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <Button autoFocus variant={current.destructive ? 'secondary' : 'primary'} danger={current.destructive} busy={running} busyLabel={current.busyLabel} onClick={() => void go()}>
                            {current.confirmLabel ?? 'OK'}
                        </Button>
                        <Button variant="secondary" disabled={running} onClick={() => settle(false)}>
                            {current.cancelLabel ?? 'Cancel'}
                        </Button>
                    </div>
                </div>
            )}
        </Sheet>
    );
}
